import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api, { apiError } from '../../services/api';
import { useApi } from '../../hooks/useApi';
import { useAppSelector } from '../../hooks/redux';
import Select from '../../components/ui/Select';
import StatusPill from '../../components/StatusPill';
import { Card, EmptyState, LoadingRows, Notice, PageHeader, SearchInput, Segmented, StatCard, Toggle } from '../../components/ui/Page';
import { ago, companyDisplayName } from '../../lib/format';
import CompanyLogo from '../../components/CompanyLogo';

export type UserRow = {
  id: string;
  name: string | null;
  email: string;
  phone: string | null;
  role: 'ADMIN' | 'CUSTOMER';
  isActive: boolean;
  disabledAt: string | null;
  lastLoginAt: string | null;
  createdAt: string;
  customerCompanies: { id: string; name: string; logicalName?: string | null; countryCode: string; status: string; isActive: boolean; hasLogo?: boolean }[];
  _count: { customerCompanies: number; assignedCompanies: number };
};

type Summary = { role: 'ADMIN' | 'CUSTOMER'; isActive: boolean; count: number };

export function Avatar({ name, email, muted }: { name: string | null; email: string; muted?: boolean }) {
  return (
    <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs font-bold ${muted ? 'bg-[#f1e6eb] text-[#b99aaa]' : 'bg-[#fdf2f8] text-[#d6006e]'}`}>
      {(name || email).charAt(0).toUpperCase()}
    </span>
  );
}

export async function setUserAccess(user: { id: string; name: string | null; email: string }, isActive: boolean) {
  if (!isActive && !window.confirm(`Disable ${user.name || user.email}? They will be signed out and cannot sign in until re-enabled.`)) return false;
  await api.patch(`/admin/users/${user.id}/access`, { isActive });
  return true;
}

export async function setUserPassword(userId: string, password: string) {
  await api.post(`/admin/users/${userId}/reset-password`, { password });
}

export default function UsersPage() {
  const navigate = useNavigate();
  const me = useAppSelector((s) => s.auth.user);
  const [q, setQ] = useState('');
  const [debounced, setDebounced] = useState('');
  const [role, setRole] = useState<'' | 'ADMIN' | 'CUSTOMER'>('');
  const [status, setStatus] = useState<'all' | 'active' | 'disabled'>('all');
  const [notice, setNotice] = useState<{ tone: 'success' | 'error'; text: string } | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  useEffect(() => {
    const t = window.setTimeout(() => setDebounced(q.trim()), 250);
    return () => window.clearTimeout(t);
  }, [q]);

  const { data, loading, error, reload } = useApi<{ users: UserRow[]; summary: Summary[] }>('/admin/users', {
    q: debounced || undefined,
    role: role || undefined,
    status: status === 'all' ? undefined : status,
  });

  const stats = useMemo(() => {
    const s = data?.summary || [];
    const sum = (f: (r: Summary) => boolean) => s.filter(f).reduce((n, r) => n + r.count, 0);
    return { total: sum(() => true), customers: sum((r) => r.role === 'CUSTOMER'), admins: sum((r) => r.role === 'ADMIN'), disabled: sum((r) => !r.isActive) };
  }, [data]);

  const toggle = async (u: UserRow, next: boolean) => {
    setBusy(u.id);
    setNotice(null);
    try {
      if (await setUserAccess(u, next)) {
        setNotice({ tone: 'success', text: `${u.name || u.email} ${next ? 'enabled' : 'disabled'}.` });
        await reload();
      }
    } catch (e) {
      setNotice({ tone: 'error', text: apiError(e, 'Could not update the user.') });
    } finally {
      setBusy(null);
    }
  };

  const users = data?.users || [];

  return (
    <div>
      <PageHeader
        title="Users"
        description="Every Micus account — customers and staff — with the companies each customer owns. Disable an account to block sign-in immediately."
        actions={<Link to="/users/new" className="mz-btn-primary">+ Create user</Link>}
      />

      <div className="mz-fade-up grid grid-cols-2 gap-3 md:grid-cols-4">
        <StatCard label="All users" value={stats.total} />
        <StatCard label="Customers" value={stats.customers} />
        <StatCard label="Admins" value={stats.admins} />
        <StatCard label="Disabled" value={stats.disabled} accent={stats.disabled > 0} />
      </div>

      <Card className="mz-fade-up mz-d1 mt-4" padded={false}>
        <div className="flex flex-wrap items-center gap-2 border-b border-[#fbeef4] p-3">
          <SearchInput value={q} onChange={setQ} placeholder="Search name, email, phone or company…" className="min-w-[240px] flex-1" />
          <Select
            value={role}
            onChange={(v) => setRole(v as typeof role)}
            className="w-44"
            options={[
              { value: '', label: 'All roles' },
              { value: 'CUSTOMER', label: 'Customers', description: 'Company owners using musb' },
              { value: 'ADMIN', label: 'Admins', description: 'Micus staff' },
            ]}
          />
          <Segmented
            value={status}
            onChange={setStatus}
            options={[
              { value: 'all', label: 'All' },
              { value: 'active', label: 'Active' },
              { value: 'disabled', label: 'Disabled' },
            ]}
          />
        </div>

        {(notice || error) && (
          <div className="px-3 pt-3">
            <Notice tone={notice?.tone || 'error'} onClose={() => setNotice(null)}>
              {notice?.text || error}
            </Notice>
          </div>
        )}

        {loading && !data ? (
          <LoadingRows />
        ) : users.length === 0 ? (
          <EmptyState title="No users match" description="Try another search or clear the filters." />
        ) : (
          <div className="overflow-x-auto">
            <table className="mz-table min-w-[760px]">
              <thead>
                <tr>
                  <th>User</th>
                  <th>Role</th>
                  <th>Companies</th>
                  <th>Last sign-in</th>
                  <th className="text-right">Access</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id} className="mz-row-link" onClick={() => navigate(`/users/${u.id}`)}>
                    <td>
                      <div className="flex items-center gap-2.5">
                        <Avatar name={u.name} email={u.email} muted={!u.isActive} />
                        <div className="min-w-0">
                          <p className={`truncate font-semibold ${u.isActive ? '' : 'text-[#b99aaa] line-through'}`}>{u.name || 'Unnamed'}</p>
                          <p className="truncate text-xs text-[#a77d92]">{u.email}</p>
                        </div>
                      </div>
                    </td>
                    <td>
                      <StatusPill label={u.role === 'ADMIN' ? 'Admin' : 'Customer'} tone={u.role === 'ADMIN' ? 'pink' : 'slate'} />
                    </td>
                    <td>
                      {u.role === 'ADMIN' ? (
                        <span className="text-xs text-[#a77d92]">{u._count.assignedCompanies} assigned</span>
                      ) : u.customerCompanies.length === 0 ? (
                        <span className="text-xs text-[#c9a3b5]">None yet</span>
                      ) : (
                        <div className="flex max-w-[320px] flex-wrap gap-1">
                          {u.customerCompanies.slice(0, 3).map((c) => (
                            <span
                              key={c.id}
                              className={`inline-flex max-w-[150px] items-center gap-1 rounded-md px-1.5 py-0.5 text-[11.5px] ${c.isActive ? 'bg-[#fdf6f9] text-[#7a1f4a]' : 'bg-[#f5eef1] text-[#b99aaa] line-through'}`}
                              style={{ border: '1px solid #f6dbe7' }}
                              title={`${companyDisplayName(c)} · ${c.status}${c.isActive ? '' : ' · disabled'}`}
                            >
                              <CompanyLogo companyId={c.id} name={companyDisplayName(c)} hasLogo={c.hasLogo} className="h-5 w-5 rounded-sm text-[9px]" />
                              <span className="truncate">{companyDisplayName(c)}</span>
                            </span>
                          ))}
                          {u.customerCompanies.length > 3 && <span className="text-[11.5px] font-semibold text-[#a77d92]">+{u.customerCompanies.length - 3}</span>}
                        </div>
                      )}
                    </td>
                    <td className="whitespace-nowrap text-xs text-[#9a6b82]">{ago(u.lastLoginAt)}</td>
                    <td className="text-right" onClick={(e) => e.stopPropagation()}>
                      <div className="inline-flex items-center gap-2">
                        <span className={`text-xs font-medium ${u.isActive ? 'text-[#047857]' : 'text-[#b99aaa]'}`}>{u.isActive ? 'Active' : 'Disabled'}</span>
                        <Toggle
                          checked={u.isActive}
                          onChange={(v) => void toggle(u, v)}
                          disabled={busy === u.id || u.id === me?.id}
                          label={u.isActive ? 'Disable user' : 'Enable user'}
                          size="sm"
                        />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
